import React, { useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Dimensions,
  Alert
} from 'react-native';
import {
  Text,
  Card,
  Title,
  Paragraph,
  TextInput,
  Button,
  Surface,
  Chip
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import {
  colors, 
  spacing, 
  borderRadius, 
  shadows, 
  typography,
  commonStyles,
  getCardMaxWidth,
  getPadding 
} from './sharedStyles';

export default function PersonalYearComponent() {
  const [birthDate, setBirthDate] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [result, setResult] = useState(null);

  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const isMobile = width < 768;

  const formatBirthDate = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  const calculatePersonalYear = (birthDateStr, targetYear) => {
    if (!birthDateStr || !targetYear) return null;
    
    const yearNum = parseInt(targetYear);
    if (isNaN(yearNum) || yearNum < 1000 || yearNum > 9999) return null;

    let month, day;
    if (birthDateStr.includes('-')) {
      const parts = birthDateStr.split('-');
      if (parts.length !== 3) return null;
      month = parseInt(parts[0]);
      day = parseInt(parts[1]);
    } else if (birthDateStr.includes('/')) {
      const parts = birthDateStr.split('/');
      if (parts.length !== 3) return null;
      month = parseInt(parts[0]);
      day = parseInt(parts[1]);
    } else {
      return null;
    }

    if (isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }

    let sum = month + day + yearNum;
    const originalSum = sum;
    
    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    }
    
    return {
      birthDate: birthDateStr,
      year: yearNum,
      personalYear: sum,
      calculation: `${month} + ${day} + ${yearNum} = ${originalSum}` + 
                   (sum !== originalSum ? ` → ${sum}` : '')
    };
  };

  const getPersonalYearMeaning = (number) => {
    const meanings = {
      1: {
        title: "Year of New Beginnings",
        description: "Time to start fresh, take initiative, and lead. Focus on independence, new projects, and pioneering efforts. This is your year to be bold and innovative.",
        themes: ["New starts", "Leadership", "Independence", "Innovation"],
        advice: "Take charge of your life, start new projects, be decisive"
      },
      2: {
        title: "Year of Cooperation",
        description: "Focus on partnerships, relationships, and working with others. Practice patience, diplomacy, and sensitivity. This year rewards collaboration over competition.",
        themes: ["Partnerships", "Patience", "Cooperation", "Diplomacy"],
        advice: "Build relationships, be patient, work as a team"
      },
      3: {
        title: "Year of Creative Expression",
        description: "Express yourself through creativity, communication, and social activities. This is a year for artistic pursuits, writing, and expanding your social circle.",
        themes: ["Creativity", "Communication", "Social life", "Self-expression"],
        advice: "Be creative, communicate openly, enjoy social activities"
      },
      4: {
        title: "Year of Hard Work",
        description: "Focus on building solid foundations through discipline and hard work. This year rewards practical efforts and attention to detail. Build for the future.",
        themes: ["Hard work", "Discipline", "Foundations", "Organization"],
        advice: "Work steadily, organize your life, build strong foundations"
      },
      5: {
        title: "Year of Freedom and Change",
        description: "Embrace change, seek new experiences, and break free from limitations. This year brings adventure, travel, and opportunities for personal growth.",
        themes: ["Change", "Freedom", "Adventure", "Growth"],
        advice: "Embrace change, take risks, seek new experiences"
      },
      6: {
        title: "Year of Responsibility",
        description: "Focus on family, home, and community responsibilities. This year emphasizes nurturing, healing, and service to others. Take care of those you love.",
        themes: ["Family", "Responsibility", "Nurturing", "Service"],
        advice: "Care for others, focus on home and family, be responsible"
      },
      7: {
        title: "Year of Inner Development",
        description: "Turn inward for spiritual growth, study, and self-reflection. This year favors meditation, research, and developing your intuition and wisdom.",
        themes: ["Spirituality", "Study", "Reflection", "Inner growth"],
        advice: "Meditate, study, trust your intuition, seek inner truth"
      },
      8: {
        title: "Year of Material Achievement",
        description: "Focus on career, business, and material success. This year rewards ambition, organization, and practical leadership. Time to achieve your goals.",
        themes: ["Success", "Achievement", "Business", "Leadership"],
        advice: "Focus on career goals, be ambitious, organize for success"
      },
      9: {
        title: "Year of Completion",
        description: "Time to complete projects, let go of the past, and prepare for a new cycle. Focus on humanitarian efforts and serving others. Release what no longer serves.",
        themes: ["Completion", "Service", "Letting go", "Wisdom"],
        advice: "Complete projects, help others, release the past"
      },
      11: {
        title: "Year of Spiritual Illumination",
        description: "A master number year bringing spiritual insight, intuition, and inspiration. Focus on your higher purpose and sharing your gifts with the world.",
        themes: ["Illumination", "Inspiration", "Intuition", "Higher purpose"],
        advice: "Trust your intuition, inspire others, follow your higher calling"
      },
      22: {
        title: "Year of Master Building",
        description: "A master number year for achieving something significant and lasting. Focus on large-scale projects and turning visions into reality.",
        themes: ["Master building", "Vision", "Achievement", "Legacy"],
        advice: "Think big, build something lasting, turn dreams into reality"
      }
    };
    
    return meanings[number] || { title: "Unknown", description: "", themes: [], advice: "" };
  };

  const handleCalculate = () => {
    if (!birthDate.trim()) {
      Alert.alert('Error', 'Please enter your birth date');
      return;
    }

    if (!year.trim()) {
      Alert.alert('Error', 'Please enter a year');
      return;
    }

    const calculation = calculatePersonalYear(birthDate, year);
    if (!calculation) {
      Alert.alert('Error', 'Please enter valid dates (MM-DD-YYYY format)');
      return;
    }

    setResult(calculation);
  };

  const resetCalculation = () => {
    setBirthDate('');
    setYear(new Date().getFullYear().toString());
    setResult(null);
  };

  return (
    <View style={commonStyles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.mainContent}>
          {/* Input Section */}
          <Surface style={[commonStyles.solidCard, styles.inputCard]}>
            {/* Header Section */}
            <View style={styles.headerContainer}>
              <LinearGradient
                colors={['rgba(74, 57, 117, 0.1)', 'rgba(102, 126, 234, 0.1)']}
                style={styles.headerIconContainer}
              >
                <Ionicons name="person-circle" size={32} color={colors.primary} />
              </LinearGradient>
              <Title style={styles.headerTitle}>Personal Year Calculator</Title>
              <Text style={styles.headerSubtitle}>
                Discover your personal numerological cycle for any year
              </Text>
            </View>

            <View style={styles.inputFieldContainer}>
              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Birth Date</Text>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={birthDate}
                      onChangeText={(text) => setBirthDate(formatBirthDate(text))}
                      selectTextOnFocus={true}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="MM-DD-YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={10}
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Year to Calculate</Text>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar-outline" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={year}
                      onChangeText={setYear}
                      onSubmitEditing={handleCalculate}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={4}
                      returnKeyType="done"
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.buttonContainer}>
                <LinearGradient
                  colors={[colors.primary, colors.primaryLight]}
                  style={styles.gradientButton}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  <Button
                    mode="contained"
                    onPress={handleCalculate}
                    style={styles.calculateButton}
                    labelStyle={styles.calculateButtonText}
                    contentStyle={styles.calculateButtonContent}
                    buttonColor="transparent"
                  >
                    <Ionicons name="analytics" size={18} color={colors.white} style={styles.buttonIcon} />
                    Calculate Personal Year
                  </Button>
                </LinearGradient>
                
                {result && (
                  <Button
                    mode="outlined"
                    onPress={resetCalculation}
                    style={styles.resetButton}
                    labelStyle={styles.resetButtonText}
                  >
                    <Ionicons name="refresh" size={16} color={colors.primary} />
                    Reset
                  </Button>
                )}
              </View>
            </View>
          </Surface>

          {result && (
            <View style={styles.resultsContainer}>
              <Surface style={[styles.resultCard, isDesktop && styles.desktopResultCard]}>
                <View style={styles.resultHeader}>
                  <Title style={styles.yearTitle}>{result.year}</Title>
                  <LinearGradient
                    colors={[colors.primary, colors.primaryLight]}
                    style={styles.personalYearBadge}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <Text style={styles.personalYearNumber}>{result.personalYear}</Text>
                    <Text style={styles.personalYearLabel}>Personal Year</Text>
                  </LinearGradient>
                </View>
                
                <Card style={styles.calculationCard}>
                  <Card.Content>
                    <Text style={styles.calculationLabel}>Calculation (Month + Day + Year):</Text>
                    <Text style={styles.calculationText}>{result.calculation}</Text>
                  </Card.Content>
                </Card>
              </Surface>

              <Card style={[styles.meaningCard, isDesktop && styles.desktopMeaningCard]}>
                <Card.Content>
                  <Title style={styles.meaningTitle}>
                    {getPersonalYearMeaning(result.personalYear).title}
                  </Title>
                  <Paragraph style={styles.meaningDescription}>
                    {getPersonalYearMeaning(result.personalYear).description}
                  </Paragraph>
                  
                  <View style={styles.themesContainer}>
                    <Text style={styles.themesTitle}>Key Themes:</Text>
                    <View style={styles.chipContainer}>
                      {getPersonalYearMeaning(result.personalYear).themes.map((theme, index) => (
                        <Chip 
                          key={index} 
                          style={styles.themeChip}
                          textStyle={styles.themeChipText}
                        >
                          {theme}
                        </Chip>
                      ))}
                    </View>
                  </View>

                  <Surface style={styles.adviceCard}>
                    <View style={styles.adviceHeader}>
                      <Ionicons name="bulb-outline" size={24} color="#4a3975" />
                      <Text style={styles.adviceTitle}>Your Personal Advice:</Text>
                    </View>
                    <Paragraph style={styles.adviceText}>
                      {getPersonalYearMeaning(result.personalYear).advice}
                    </Paragraph>
                  </Surface>

                  <Surface style={styles.timelineCard}>
                    <View style={styles.timelineHeader}>
                      <Ionicons name="time-outline" size={24} color="#4a3975" />
                      <Text style={styles.timelineTitle}>When Your Personal Year Begins:</Text>
                    </View>
                    <Paragraph style={styles.timelineText}>
                      Your personal year runs from your birthday in {result.year} to your birthday in {result.year + 1}. 
                      The energy of this cycle will be most potent during the first half of the year.
                    </Paragraph>
                  </Surface>
                </Card.Content>
              </Card>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: spacing.lg,
  },
  mainContent: {
    alignItems: 'center',
    paddingBottom: spacing.xxl,
  },
  
  // Input Section
  inputCard: {
    width: '100%',
    maxWidth: getCardMaxWidth(600),
    alignSelf: 'center',
    marginBottom: spacing.xxl,
    padding: spacing.xxl,
  },
  
  // Header Section
  headerContainer: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: {
    ...typography.heading1,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  headerSubtitle: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 22,
  },
  
  // Input Section
  inputFieldContainer: {
    gap: spacing.xl,
  },
  fieldWrapper: {
    gap: spacing.sm,
  },
  fieldLabel: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
    marginLeft: spacing.sm,
  },
  inputWrapper: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.input,
    overflow: 'hidden',
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    paddingVertical: spacing.xs,
  },
  inputIcon: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 1,
  },
  modernInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingLeft: spacing.xxxl + spacing.sm,
    elevation: 0,
    shadowOpacity: 0,
  },
  
  // Button Section
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },
  gradientButton: {
    borderRadius: borderRadius.xl,
    ...shadows.button,
    flex: 1,
    maxWidth: 300,
  },
  calculateButton: {
    backgroundColor: 'transparent',
    elevation: 0,
    shadowOpacity: 0,
  },
  calculateButtonContent: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  calculateButtonText: {
    ...typography.button,
    color: colors.white,
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
  resetButton: {
    borderColor: colors.primary,
    borderRadius: borderRadius.xl,
    backgroundColor: 'transparent',
  },
  resetButtonText: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
  },
  
  // Results Section
  resultsContainer: {
    width: '100%',
    maxWidth: getCardMaxWidth(700),
    alignSelf: 'center',
    gap: spacing.xxl,
  },
  resultCard: {
    ...commonStyles.resultCard,
    alignItems: 'center',
  },
  resultHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  yearTitle: {
    ...typography.heading1,
    fontSize: 42,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  personalYearBadge: {
    borderRadius: 35,
    paddingHorizontal: spacing.xxxl,
    paddingVertical: spacing.xl,
    alignItems: 'center',
    marginBottom: spacing.lg,
    ...shadows.result,
    overflow: 'hidden',
  },
  personalYearNumber: {
    ...typography.heading1,
    fontSize: 52,
    color: colors.white,
    fontWeight: '800',
  },
  personalYearLabel: {
    ...typography.caption,
    color: colors.white,
    fontWeight: '600',
    marginTop: spacing.xs,
  },
  calculationCard: {
    backgroundColor: colors.primary + '10',
    borderRadius: borderRadius.lg,
    ...shadows.input,
  },
  calculationLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  calculationText: {
    ...typography.heading3,
    color: colors.primary,
  },
  meaningCard: {
    ...commonStyles.resultCard,
  },
  meaningTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  meaningDescription: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xxl,
  },
  themesContainer: {
    marginBottom: spacing.xxl,
  },
  themesTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  themeChip: {
    backgroundColor: colors.primary + '15',
    borderRadius: borderRadius.pill,
    ...shadows.input,
  },
  themeChipText: {
    color: colors.primary,
    fontWeight: '600',
  },
  adviceCard: {
    backgroundColor: colors.primary + '05',
    borderRadius: borderRadius.lg,
    padding: spacing.xl,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    marginBottom: spacing.lg,
  },
  adviceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  adviceTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginLeft: spacing.md,
  },
  adviceText: {
    ...typography.body,
    color: colors.textSecondary,
    lineHeight: 24,
  },
  timelineCard: {
    backgroundColor: colors.primaryLight + '05',
    borderRadius: borderRadius.lg,
    padding: spacing.xl,
    borderLeftWidth: 4,
    borderLeftColor: colors.primaryLight,
  },
  timelineHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  timelineTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginLeft: spacing.md,
  },
  timelineText: {
    ...typography.body,
    color: colors.textSecondary,
    lineHeight: 24,
  },
});